import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, BadgeCheckIcon, ClockIcon, Loader2Icon, XCircleIcon } from 'lucide-react';
import { useStore } from '../../contexts/StoreContext';
import { Field } from '../ui/Field';
import { LeadSuccess } from '../LeadSuccess';
import { Lead, ScoringResult } from '../../types/lead';
import { cleanText, formatCuil, isValidCuil, isValidEmail, isValidPhone, normalizePhone } from '../../utils/validation';
import { newScoringCode, requestScoring, scoringLabels } from '../../utils/scoring';
type Errors = Partial<Record<'cuil' | 'phone' | 'email' | 'consent' | 'form', string>>;
const resultStyle: Record<ScoringResult, {
  box: string;
  text: string;
  Icon: typeof BadgeCheckIcon;
  note: string;
}> = {
  PRE_APROBADO: {
    box: 'border-ok/50 bg-ok/10',
    text: 'text-ok',
    Icon: BadgeCheckIcon,
    note: 'Según la Central de Deudores del BCRA no registrás deudas en mora.'
  },
  MOROSIDAD_ACTIVA: {
    box: 'border-warn/50 bg-warn/10',
    text: 'text-warn',
    Icon: AlertTriangleIcon,
    note: 'Registrás atrasos informados por al menos una entidad. Un asesor puede ayudarte a ver qué alternativas tenés.'
  },
  RECHAZADO: {
    box: 'border-danger/50 bg-danger/10',
    text: 'text-danger',
    Icon: XCircleIcon,
    note: 'Con la situación informada hoy no es posible avanzar con una financiación. Un asesor puede orientarte.'
  },
  PENDIENTE: {
    box: 'border-neon/50 bg-neon/10',
    text: 'text-neon-hi',
    Icon: ClockIcon,
    note: 'No pudimos completar la consulta automática. Un asesor la revisa y te contacta.'
  }
};
export function QuickScoringForm() {
  const {
    addLead
  } = useStore();
  const [cuil, setCuil] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [done, setDone] = useState<{
    lead: Lead;
    result: ScoringResult;
  } | null>(null);
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    const er: Errors = {};
    if (!isValidCuil(cuil)) er.cuil = 'Ingresá un CUIL válido (11 dígitos).';
    if (!isValidPhone(phone)) er.phone = 'Ingresá un teléfono con código de área.';
    if (!isValidEmail(email)) er.email = 'Ingresá un email válido.';
    if (!consent) er.consent = 'Necesitamos tu autorización para consultar.';
    setErrors(er);
    if (Object.keys(er).length) return;
    setLoading(true);
    const code = newScoringCode();
    const at = new Date().toISOString();
    const digits = cuil.replace(/\D/g, '');
    const res = await requestScoring({
      codigo_publico: code,
      cuil: digits,
      telefono: normalizePhone(phone),
      email: cleanText(email, 120),
      consentimiento: 'SI',
      fecha_consentimiento: at
    });
    setLoading(false);
    if (!res.ok && res.code !== 'NETWORK') {
      setErrors({
        form: res.error
      });
      return;
    }
    // Sin respuesta válida del BCRA: se pide reintentar. Recién al 3.er intento fallido queda "en revisión".
    const failed = !res.ok || res.result === 'PENDIENTE';
    if (failed && attempts < 2) {
      setAttempts(n => n + 1);
      setErrors({
        form: 'El BCRA no respondió a tiempo. Tocá "Ver mi scoring" de nuevo en unos segundos.'
      });
      return;
    }
    const result: ScoringResult = res.ok ? res.result : 'PENDIENTE';
    const lead = addLead({
      code,
      channel: 'scoring',
      name: 'Scoring rápido',
      phone: normalizePhone(phone),
      email: cleanText(email, 120),
      contactPref: 'whatsapp',
      vehicleId: null,
      vehicleLabel: null,
      price: null,
      down: null,
      term: null,
      simulation: null,
      message: `Scoring rápido BCRA: ${scoringLabels[result]}.`,
      credit: null,
      cuil: digits,
      scoring: result,
      consents: {
        dataProcessing: true,
        contact: true,
        at
      }
    });
    setDone({
      lead,
      result
    });
  };
  if (done) {
    const s = resultStyle[done.result];
    return <div className="space-y-4" data-id="element-508" data-mp="23xz1igzdug3z">
        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className={`rounded-2xl border p-5 text-center ${s.box}`} role="status" data-id="element-509" data-mp="2iphee5wy3uj4">
          <s.Icon className={`mx-auto h-9 w-9 ${s.text}`} aria-hidden data-id="element-510" data-mp="1l1fq1l13wg49" />
          <p className={`mt-2 font-display text-3xl font-black tracking-tight ${s.text}`} data-id="element-511" data-mp="1l3adp73rimc1">{scoringLabels[done.result]}</p>
          <p className="mt-2 text-sm text-silver-hi" data-id="element-512" data-mp="1uzddw7f20awc">{s.note}</p>
          <p className="mt-2 text-xs text-mist" data-id="element-513" data-mp="1uy28y49vgx3n">También te enviamos el resultado a {done.lead.email}.</p>
        </motion.div>
        <LeadSuccess lead={done.lead} data-id="element-514" data-mp="3bfg7cce9gbt2">Un asesor te contactará para avanzar con las alternativas de financiación para tu 0 km.</LeadSuccess>
        <p className="text-[11px] text-mist" data-id="element-515" data-mp="3dxs0hylbhqj9">
          Resultado orientativo basado en la información pública de la Central de Deudores del BCRA, que se actualiza mensualmente. No constituye una aprobación crediticia: la
          decisión final corresponde a la entidad financiera.
        </p>
      </div>;
  }
  return <form onSubmit={onSubmit} noValidate className="space-y-4" data-id="element-516" data-mp="38zgr1oyxpi1k">
      <p className="text-sm text-mist" data-id="element-517" data-mp="ivexzb0a8u8">Conocé en segundos tu situación para financiar tu 0 km. Sólo necesitamos tu CUIL y dónde enviarte el resultado.</p>
      <Field label="CUIL" htmlFor="sc-cuil" error={errors.cuil} data-id="element-518" data-mp="5kciy3osebf1">
        <input id="sc-cuil" className="input font-mono" inputMode="numeric" placeholder="20-12345678-9" value={cuil} onChange={e => setCuil(formatCuil(e.target.value))} data-id="element-519" data-mp="3nvg0b2xs5o8a" />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-520" data-mp="jgyakwy27410">
        <Field label="Teléfono" htmlFor="sc-phone" error={errors.phone} data-id="element-521" data-mp="jg6d1gkwiw83">
          <input id="sc-phone" className="input" inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)} autoComplete="tel" maxLength={20} data-id="element-522" data-mp="oaapgfcxrwgk" />
        </Field>
        <Field label="Email" htmlFor="sc-email" error={errors.email} data-id="element-523" data-mp="u461vxli25yj">
          <input id="sc-email" type="email" className="input" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" maxLength={120} data-id="element-524" data-mp="p3ru8gnt8y11" />
        </Field>
      </div>
      <label className="flex items-start gap-3 rounded-xl border border-line p-3 text-xs text-mist" data-id="element-525" data-mp="f5u57qy4mx7d">
        <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4" aria-invalid={!!errors.consent} data-id="element-526" data-mp="af5e4cqd028r" />
        <span data-id="element-527" data-mp="6e1ynrc84882">
          Soy el titular de este CUIL y autorizo a VCARS a consultar mi situación en la Central de Deudores del BCRA y a tratar estos datos para evaluar alternativas de
          financiación y contactarme, conforme a la Ley 25.326. Puedo solicitar su acceso, rectificación o supresión en cualquier momento.
        </span>
      </label>
      {errors.consent && <p className="-mt-2 text-xs text-danger" role="alert" data-id="element-528" data-mp="3diiblhrzi9ed">{errors.consent}</p>}
      {errors.form && <p className="rounded-lg border border-danger/40 bg-danger/10 p-3 text-sm text-danger" role="alert" data-id="element-529" data-mp="3ig0mhdpkayp0">{errors.form}</p>}
      <button type="submit" disabled={loading} className="btn btn-primary h-12 w-full disabled:opacity-70" data-id="element-530" data-mp="3nh7sp8c23ptk">
        {loading ? <>
            <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden data-id="element-531" data-mp="2ur91p7eaxim9" />
            Consultando…
          </> : 'Ver mi scoring'}
      </button>
    </form>;
}